import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faClock, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import Loading from '../Loading';
import academicService from '../../services/academicService';
import academicAreaService from '../../services/academicAreaService';
import gradesService from '../../services/gradesService';
import courseService from '../../services/courseService';
import collectionsService from '../../services/collectionsService';
import schoolSubjectPercentageService from '../../services/schoolSubjectPercentageService';
import schoolSubjectPercentagePeriodService from '../../services/schoolSubjectPercentagePeriodService';
import schoolSubjectPercentagePeriodGradesService from '../../services/schoolSubjectPercentagePeriodGradesService';
import useCustomDataTableStyles from '../estilosDeDataTable';

const emptyToNull = (value) => (value === '' || value === undefined ? null : value);

const describeChanges = (item) => {
    const changes = [];
    if (item.academicAreaId) changes.push(`el área académica cambia a "${item.academicAreaName}"`);
    if (item.percentage !== null && item.percentage !== undefined && item.percentage !== '') changes.push(`el porcentaje del área académica cambia a ${item.percentage}%`);
    if (item.hourlyIntensity !== null && item.hourlyIntensity !== undefined && item.hourlyIntensity !== '') changes.push(`la intensidad horaria cambia a ${item.hourlyIntensity}`);
    if (item.isVisible === false) changes.push('la asignatura queda OCULTA en el boletín');
    return changes;
};

const NivelSection = (props) => {
    const {
        title, priorityBadge, priorityExplanation, service, schoolSubjectId, schoolYearId,
        hasPeriod, hasEducationLevel, hasGrade, hasHourlyIntensity,
        journeys, academicAreas, periods, educationLevels, grades
    } = props;

    const emptyForm = {
        schoolSubjectPercentageId: 0,
        schoolSubjectId,
        schoolYearId,
        journeyId: '',
        academicPeriodId: '',
        educationLevelId: '',
        gradeId: '',
        academicAreaId: '',
        percentage: '',
        hourlyIntensity: '',
        isVisible: true
    };

    const customStyles = useCustomDataTableStyles();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState(emptyForm);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const data = await service.getList(schoolSubjectId, schoolYearId);
            setItems(data || []);
        } catch (error) {
            console.error(`Error al obtener personalizaciones (${title}):`, error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schoolSubjectId, schoolYearId]);

    const openAddForm = () => {
        setFormData({ ...emptyForm, schoolSubjectId, schoolYearId });
        setShowForm(true);
    };

    const openEditForm = (item) => {
        setFormData({
            schoolSubjectPercentageId: item.schoolSubjectPercentageId,
            schoolSubjectId,
            schoolYearId,
            journeyId: item.journeyId ?? '',
            academicPeriodId: item.academicPeriodId ?? '',
            educationLevelId: item.educationLevelId ?? '',
            gradeId: item.gradeId ?? '',
            academicAreaId: item.academicAreaId ?? '',
            percentage: item.percentage ?? '',
            hourlyIntensity: item.hourlyIntensity ?? '',
            isVisible: item.isVisible !== false
        });
        setShowForm(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleDelete = (item) => {
        Swal.fire({
            title: '¿Eliminar esta personalización?',
            text: 'La asignatura volverá a usar el valor del nivel de menor prioridad.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Si, eliminar!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await service.deleteById(item.schoolSubjectPercentageId);
                    await fetchItems();
                } catch (error) {
                    Swal.fire('Ha ocurrido un error', 'Por favor intentelo mas tarde', 'error');
                }
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (hasPeriod && !formData.academicPeriodId) {
            Swal.fire('Campo obligatorio', 'Debe seleccionar el periodo académico.', 'error');
            return;
        }
        if (hasGrade && !formData.gradeId) {
            Swal.fire('Campo obligatorio', 'Debe seleccionar el grado.', 'error');
            return;
        }

        const payload = {
            schoolSubjectPercentageId: formData.schoolSubjectPercentageId,
            schoolSubjectId,
            schoolYearId,
            journeyId: emptyToNull(formData.journeyId),
            academicAreaId: emptyToNull(formData.academicAreaId),
            percentage: formData.percentage === '' ? null : Number(formData.percentage),
            isVisible: !!formData.isVisible
        };
        if (hasPeriod) payload.academicPeriodId = Number(formData.academicPeriodId);
        if (hasEducationLevel) payload.educationLevelId = emptyToNull(formData.educationLevelId);
        if (hasGrade) payload.gradeId = Number(formData.gradeId);
        if (hasHourlyIntensity) payload.hourlyIntensity = formData.hourlyIntensity === '' ? null : Number(formData.hourlyIntensity);

        try {
            if (payload.schoolSubjectPercentageId > 0) {
                await service.update(payload);
            } else {
                await service.create(payload);
            }
            setShowForm(false);
            await fetchItems();
        } catch (error) {
            const message = error?.response?.data?.message || 'Por favor intentelo mas tarde';
            Swal.fire('Ha ocurrido un error', message, 'error');
        }
    };

    const columns = [
        { name: 'Jornada', selector: (row) => row.journeyName || 'Todas', sortable: true },
        ...(hasPeriod ? [{ name: 'Periodo', selector: (row) => row.academicPeriodName || '—', sortable: true, grow: 1.5 }] : []),
        ...(hasEducationLevel ? [{ name: 'Nivel educativo', selector: (row) => row.educationLevelName || 'Todos', sortable: true, grow: 1.5 }] : []),
        ...(hasGrade ? [{ name: 'Grado', selector: (row) => row.gradeName || '—', sortable: true }] : []),
        { name: 'Área académica', selector: (row) => row.academicAreaName || '— sin cambio —', sortable: true, grow: 1.5 },
        { name: 'Porcentaje', selector: (row) => (row.percentage !== null && row.percentage !== undefined ? `${row.percentage}%` : '— sin cambio —'), sortable: true },
        ...(hasHourlyIntensity ? [{ name: 'Int. horaria', selector: (row) => (row.hourlyIntensity ?? '— sin cambio —'), sortable: true }] : []),
        {
            name: 'Visible', selector: (row) => row.isVisible, sortable: true,
            cell: (row) => (row.isVisible === false ? <span className="text-danger fw-bold">No</span> : <span>Si</span>)
        },
        {
            name: 'Acciones', grow: 1,
            cell: (row) => (
                <div className="actions-container justify-content-between align-items-center">
                    <button onClick={() => openEditForm(row)} className="btn-edit btn-sm me-2" title="Editar">
                        <i className="icon-edit fas fa-pencil-alt"></i>
                    </button>
                    <button onClick={() => handleDelete(row)} className="btn-deactivate btn-sm" title="Eliminar">
                        <i className="icon-deactivate fas fa-times"></i>
                    </button>
                </div>
            )
        }
    ];

    const previewItem = {
        academicAreaId: formData.academicAreaId,
        academicAreaName: academicAreas.find(a => String(a.academicAreaId) === String(formData.academicAreaId))?.name,
        percentage: formData.percentage,
        hourlyIntensity: formData.hourlyIntensity,
        isVisible: formData.isVisible
    };
    const previewChanges = describeChanges(previewItem);

    return (
        <div className="p-2">
            <div className="alert alert-secondary py-2 px-3 mb-3">{priorityExplanation}</div>

            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">{title}</h6>
                {!showForm && (
                    <button type="button" className="btn btn-sm btn-Estudiante btn-NuevoEstudiante" onClick={openAddForm}>
                        Agregar personalización <i className="fa-solid fa-circle-plus"></i>
                    </button>
                )}
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="border rounded p-3 mb-3 bg-light">
                    <div className="row">
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Jornada</label>
                            <select className="form-control form-select" name="journeyId" value={formData.journeyId} onChange={handleChange}>
                                <option value="">Todas las jornadas</option>
                                {journeys.map((journey) => (
                                    <option key={journey.journeyId} value={journey.journeyId}>{journey.name}</option>
                                ))}
                            </select>
                        </div>

                        {hasPeriod && (
                            <div className="col-md-3 mb-3">
                                <label className="form-label">Periodo académico:*</label>
                                <select className="form-control form-select" name="academicPeriodId" value={formData.academicPeriodId} onChange={handleChange} required>
                                    <option value="">Seleccione</option>
                                    {periods.map((period) => (
                                        <option key={period.academicPeriodId} value={period.academicPeriodId}>{period.periodName || period.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {hasEducationLevel && (
                            <div className="col-md-3 mb-3">
                                <label className="form-label">Nivel educativo</label>
                                <select className="form-control form-select" name="educationLevelId" value={formData.educationLevelId} onChange={handleChange}>
                                    <option value="">Todos los niveles</option>
                                    {educationLevels.map((level) => (
                                        <option key={level.educationLevelId} value={level.educationLevelId}>{level.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {hasGrade && (
                            <div className="col-md-3 mb-3">
                                <label className="form-label">Grado:*</label>
                                <select className="form-control form-select" name="gradeId" value={formData.gradeId} onChange={handleChange} required>
                                    <option value="">Seleccione</option>
                                    {grades.map((grade) => (
                                        <option key={grade.gradeId} value={grade.gradeId}>{grade.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="col-md-3 mb-3">
                            <label className="form-label">Área académica</label>
                            <select className="form-control form-select" name="academicAreaId" value={formData.academicAreaId} onChange={handleChange}>
                                <option value="">No personalizar</option>
                                {academicAreas.map((area) => (
                                    <option key={area.academicAreaId} value={area.academicAreaId}>{area.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-3 mb-3">
                            <label className="form-label">Porcentaje del área académica</label>
                            <input type="number" step="0.01" className="form-control" name="percentage" placeholder="No personalizar" value={formData.percentage} onChange={handleChange} />
                        </div>

                        {hasHourlyIntensity && (
                            <div className="col-md-3 mb-3">
                                <label className="form-label">Intensidad horaria</label>
                                <input type="number" className="form-control" name="hourlyIntensity" placeholder="No personalizar" value={formData.hourlyIntensity} onChange={handleChange} />
                            </div>
                        )}

                        <div className="col-md-3 mb-3 d-flex align-items-end">
                            <div className="form-check">
                                <input type="checkbox" className="form-check-input" id={`isVisible-${priorityBadge}`} name="isVisible" checked={formData.isVisible} onChange={handleChange} />
                                <label className="form-check-label" htmlFor={`isVisible-${priorityBadge}`}>Visible en el boletín</label>
                            </div>
                        </div>
                    </div>

                    <div className="alert alert-info py-2 px-3">
                        <strong>Vista previa:</strong> {previewChanges.length > 0 ? 'se aplicará: ' + previewChanges.join('; ') + '.' : 'no se ha configurado ningún cambio todavía.'}
                    </div>

                    <div className="d-flex">
                        <button type="submit" className="btn btn-primary me-2">Guardar</button>
                        <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                    </div>
                </form>
            )}

            {loading && <Loading />}

            {!loading && (
                <DataTable
                    className="students-datatable shadow-sm rounded"
                    noDataComponent="No hay personalizaciones configuradas en este nivel."
                    columns={columns}
                    data={items}
                    pagination
                    customStyles={customStyles}
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50]}
                />
            )}
        </div>
    );
};

const PersonalizarAsignaturaModal = ({ schoolSubjectId, schoolSubjectName }) => {
    const [showLoading, setShowLoading] = useState(true);
    const [years, setYears] = useState([]);
    const [journeys, setJourneys] = useState([]);
    const [academicAreas, setAcademicAreas] = useState([]);
    const [grades, setGrades] = useState([]);
    const [educationLevels, setEducationLevels] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [schoolYearId, setSchoolYearId] = useState('');
    const [activeTab, setActiveTab] = useState('periodoGrado');
    const [showInfoAlert, setShowInfoAlert] = useState(true);

    const loadPeriods = async (yearId) => {
        if (!yearId) {
            setPeriods([]);
            return;
        }
        try {
            const schoolYear = await academicService.getYearById(yearId);
            setPeriods(schoolYear?.periods || []);
        } catch (error) {
            console.error('Error al obtener periodos del año escolar:', error);
        }
    };

    const fetchCollections = async () => {
        setShowLoading(true);
        try {
            const [yearsResp, journeysResp, areasResp, gradesResp, levelsResp, parameters] = await Promise.all([
                academicService.getYears(),
                academicService.getJourneys(),
                academicAreaService.getAllAcademicAreas(),
                gradesService.getAllGrades(),
                courseService.getEducationLevels(),
                collectionsService.getParameters()
            ]);
            setYears(yearsResp || []);
            setJourneys(journeysResp || []);
            setAcademicAreas(areasResp || []);
            setGrades(gradesResp || []);
            setEducationLevels(levelsResp || []);

            const currentYearParam = (parameters || []).find((op) => String(op.keyName) === 'AnioElectivo');
            const initialYearId = currentYearParam ? currentYearParam.value : (yearsResp?.[0]?.schoolYearId || '');
            setSchoolYearId(initialYearId);
            await loadPeriods(initialYearId);
        } catch (error) {
            console.error('Error al obtener listas de formulario:', error);
        }
        setShowLoading(false);
    };

    useEffect(() => {
        fetchCollections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleYearChange = async (e) => {
        const value = e.target.value;
        setSchoolYearId(value);
        await loadPeriods(value);
    };

    return (
        <div className="container-fluid mt-2">
            {showLoading && <Loading />}

            {!showLoading && (
                <>
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label className="form-label">Año escolar a personalizar:</label>
                            <select className="form-control form-select" value={schoolYearId} onChange={handleYearChange}>
                                {years.map((year) => (
                                    <option key={year.schoolYearId} value={year.schoolYearId}>{year.year}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {showInfoAlert && (
                        <div className="alert alert-primary alert-dismissible fade show" style={{ flexDirection: 'column' }} role="alert">
                            <strong>Prioridad de aplicación en el boletín para "{schoolSubjectName}".</strong>
                            Cuando existan varias personalizaciones que coincidan para la misma asignatura, siempre gana la más específica.<br />
                            Orden de mayor a menor prioridad: <strong>1) Periodo + Grado</strong> <strong>2) Periodo</strong> <strong>3) Año Escolar</strong> y por último la configuración base de la asignatura.<br />
                            Las pestañas de abajo están ordenadas siguiendo esa misma prioridad, de izquierda a derecha.
                            <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setShowInfoAlert(false)}></button>
                        </div>
                    )}

                    {schoolYearId && (
                        <div className="tab">
                            <ul className="nav nav-tabs" role="tablist">
                                {/* Periodo + Grado Tab */}
                                <li className="nav-item" role="presentation">
                                    <a className={`nav-link ${activeTab === 'periodoGrado' ? 'active' : ''}`}
                                        id="periodoGrado-tab"
                                        data-bs-toggle="tab"
                                        role="tab"
                                        aria-controls="periodoGrado"
                                        aria-selected={activeTab === 'periodoGrado'}
                                        onClick={() => setActiveTab('periodoGrado')}
                                    >
                                        <FontAwesomeIcon icon={faLayerGroup} className="icon-hover" /> Periodo + Grado
                                    </a>
                                </li>
                                {/* Periodo Tab */}
                                <li className="nav-item" role="presentation">
                                    <a className={`nav-link ${activeTab === 'periodo' ? 'active' : ''}`}
                                        id="periodo-tab"
                                        data-bs-toggle="tab"
                                        role="tab"
                                        aria-controls="periodo"
                                        aria-selected={activeTab === 'periodo'}
                                        onClick={() => setActiveTab('periodo')}
                                    >
                                        <FontAwesomeIcon icon={faClock} className="icon-hover" /> Periodo
                                    </a>
                                </li>
                                {/* Año Escolar Tab */}
                                <li className="nav-item" role="presentation">
                                    <a className={`nav-link ${activeTab === 'anioEscolar' ? 'active' : ''}`}
                                        id="anioEscolar-tab"
                                        data-bs-toggle="tab"
                                        role="tab"
                                        aria-controls="anioEscolar"
                                        aria-selected={activeTab === 'anioEscolar'}
                                        onClick={() => setActiveTab('anioEscolar')}
                                    >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="icon-hover" /> Año Escolar
                                    </a>
                                </li>
                            </ul>

                            <div className="tab-content">
                                {/* Panel de Periodo + Grado */}
                                <div className={`tab-pane ${activeTab === 'periodoGrado' ? 'active show' : ''}`}
                                    id="periodoGrado"
                                    role="tabpanel"
                                    aria-labelledby="periodoGrado-tab"
                                >
                                    <NivelSection
                                        title="Personalización por Periodo + Grado"
                                        priorityBadge={1}
                                        priorityExplanation="Máxima prioridad: se aplica solo al grado y periodo indicados, ignorando lo configurado en los niveles de Periodo y Año Escolar."
                                        service={schoolSubjectPercentagePeriodGradesService}
                                        schoolSubjectId={schoolSubjectId}
                                        schoolYearId={schoolYearId}
                                        hasPeriod
                                        hasGrade
                                        hasHourlyIntensity
                                        journeys={journeys}
                                        academicAreas={academicAreas}
                                        periods={periods}
                                        educationLevels={educationLevels}
                                        grades={grades}
                                    />
                                </div>

                                {/* Panel de Periodo */}
                                <div className={`tab-pane ${activeTab === 'periodo' ? 'active show' : ''}`}
                                    id="periodo"
                                    role="tabpanel"
                                    aria-labelledby="periodo-tab"
                                >
                                    <NivelSection
                                        title="Personalización por Periodo"
                                        priorityBadge={2}
                                        priorityExplanation="Se aplica a todos los grados del nivel educativo indicado (o a todos si no se elige uno) durante ese periodo, salvo que exista una personalización más específica de Periodo + Grado."
                                        service={schoolSubjectPercentagePeriodService}
                                        schoolSubjectId={schoolSubjectId}
                                        schoolYearId={schoolYearId}
                                        hasPeriod
                                        hasEducationLevel
                                        hasHourlyIntensity
                                        journeys={journeys}
                                        academicAreas={academicAreas}
                                        periods={periods}
                                        educationLevels={educationLevels}
                                        grades={grades}
                                    />
                                </div>

                                {/* Panel de Año Escolar */}
                                <div className={`tab-pane ${activeTab === 'anioEscolar' ? 'active show' : ''}`}
                                    id="anioEscolar"
                                    role="tabpanel"
                                    aria-labelledby="anioEscolar-tab"
                                >
                                    <NivelSection
                                        title="Personalización por Año Escolar"
                                        priorityBadge={3}
                                        priorityExplanation="Menor prioridad: aplica durante todo el año escolar y todos los periodos, salvo que exista una personalización más específica de Periodo o Periodo + Grado. No permite cambiar la intensidad horaria, solo el área académica, el porcentaje y la visibilidad."
                                        service={schoolSubjectPercentageService}
                                        schoolSubjectId={schoolSubjectId}
                                        schoolYearId={schoolYearId}
                                        journeys={journeys}
                                        academicAreas={academicAreas}
                                        periods={periods}
                                        educationLevels={educationLevels}
                                        grades={grades}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PersonalizarAsignaturaModal;
